import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShoppingBag } from "lucide-react";
import { api } from "../lib/api";
import { ShopHeader } from "../components/site/ShopHeader";
import { useCart } from "../contexts/CartContext";

export default function Shop() {
  const [products, setProducts] = useState(null);
  const { add } = useCart();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#F6F5F0]">
      <ShopHeader />
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <span className="inline-flex items-center gap-2 text-[#3A2718] text-xs font-semibold uppercase tracking-[0.2em] mb-4">
          <span className="w-8 h-px bg-[#3A2718]" /> Boreal Coco Shop
        </span>
        <h1 className="font-display font-bold text-4xl sm:text-5xl text-[#1A1C19]">Coconut products, delivered.</h1>
        <p className="mt-4 text-[#4A4D48] text-lg max-w-2xl">Browse our coconut grow media, food ingredients, retail and industrial products.</p>

        {products === null && <p className="mt-12 text-[#4A4D48]">Loading products…</p>}
        {products && products.length === 0 && (
          <div className="mt-16 rounded-2xl bg-white border border-[#D1CFC8] p-16 text-center text-[#4A4D48]" data-testid="shop-empty">
            <ShoppingBag className="w-10 h-10 mx-auto mb-4" />
            No products yet. Please check back soon.
          </div>
        )}

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="product-grid">
          {products?.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (i % 3) * 0.08 }}
              className="group rounded-2xl bg-white border border-[#D1CFC8] overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-400 flex flex-col"
              data-testid={`product-${p.id}`}
            >
              <div className="relative h-56 bg-[#ECE4D6] overflow-hidden">
                {p.image && <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                {p.category && <span className="absolute top-3 left-3 text-xs font-semibold uppercase tracking-wide text-[#1F5A34] bg-[#F6F5F0] px-3 py-1 rounded-full">{p.category}</span>}
                {p.stock <= 0 && !p.amazon_url && <span className="absolute top-3 right-3 text-xs font-semibold text-white bg-red-600/90 px-3 py-1 rounded-full">Sold out</span>}
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-display font-bold text-xl text-[#1A1C19]">{p.name}</h3>
                <p className="mt-2 text-[#4A4D48] text-sm line-clamp-2 flex-1">{p.description}</p>
                <div className="mt-5 flex items-center justify-between">
                  <span className="font-display font-bold text-2xl text-[#1F5A34]" data-testid={`price-${p.id}`}>${p.price.toFixed(2)}</span>
                  {p.amazon_url ? (
                    <a
                      href={p.amazon_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`buy-amazon-${p.id}`}
                      className="px-5 py-2.5 rounded-full bg-[#FF9900] text-[#131921] text-sm font-semibold hover:bg-[#f3a847] transition-colors"
                    >
                      Buy on Amazon
                    </a>
                  ) : (
                    <button
                      onClick={() => add(p)}
                      disabled={p.stock <= 0}
                      data-testid={`add-to-cart-${p.id}`}
                      className="px-5 py-2.5 rounded-full bg-[#1F5A34] text-[#F6F5F0] text-sm font-semibold hover:bg-[#163F25] transition-colors disabled:opacity-40"
                    >
                      Add to cart
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
